import React from 'react';
import PropTypes from 'prop-types';
import {
  useRouteMatch,
  Switch,
  Route,
  useLocation,
  useParams,
} from 'react-router-dom';
import { BiHome } from 'react-icons/bi';

import history from 'setup/history';
import Button from 'partial/components/Button';
import PageNotFound from 'partial/containers/PageNotFound';
import Version from 'partial/components/Version';
import IconAccountVerified from 'assets/images/icons/icon-check-success.svg';
import IconInvalidActivation from 'assets/images/icons/icon-invalid-activation.svg';
import IconInvalidExpired from 'assets/images/icons/icon-invalid-expired.svg';
import IconVerifyEmail from 'assets/images/icons/icon-verify-email.svg';
import { SolidLogo } from 'modules/common/components/AppIconLogo';
import { useResendEmail } from '../hooks';

const ThisComponentWrapper = ({ children }) => {
  return (
    <div className="h-screen flex flex-col bg-gradient-to-t from-primary-700 to-primary-800">
      <div className="h-screen flex flex-col items-center justify-center bg-dotted-effect bg-login relative">
        <div className="absolute top-5 left-5 uppercase">
          <Button outlineLight icon={BiHome} onClick={() => history.push('/')}>
            Back to homepage
          </Button>
        </div>
        <div className="flex justify-center mb-6 h-40 relative">
          <SolidLogo className="h-40" />
          <p className="absolute bottom-9 ml-20 text-xs text-white whitespace-nowrap">
            BILLER PORTAL
          </p>
        </div>
        <main className="mt-8 mx-2 flex flex-col items-center text-center bg-white rounded-2xl p-4 sm:p-6 lg:p-10 w-full max-w-xl">
          {children}
        </main>
        <div className="text-xs font-light mt-10 text-center text-gray-400">
          &copy; 2021 DST Biller Portal <Version />
        </div>
      </div>
    </div>
  );
};

const ActivateSuccess = () => {
  return (
    <ThisComponentWrapper>
      <img src={IconAccountVerified} alt="verified" className="w-36" />
      <div className="flex flex-col border-b py-4">
        <h1 className="font-semibold text-lg text-gray-800 tracking-wide">
          Account Verified
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Congratulations! Your account has been successfully verified.
        </p>
        <p className="mt-2 text-sm text-gray-600">
          You can now login to your account.
        </p>
      </div>

      <Button
        className="mt-6 mb-5"
        outlinePrimary
        onClick={() => history.push('/')}
      >
        Proceed to Login
      </Button>
    </ThisComponentWrapper>
  );
};

function ActivateExpired() {
  const { id } = useParams();
  const [isResendLoading, resendEmail] = useResendEmail(
    id,
    (res) => {
      history.push({
        pathname: `/email-activation/resend/${res?.data?.uuid}`,
        state: { email_address: res?.data?.receiver || '' },
      });
    },
    () => {}
  );
  const handleResendEmail = () => {
    resendEmail();
  };
  return (
    <ThisComponentWrapper>
      <img src={IconInvalidExpired} alt="expired" className="w-36" />
      <div className="flex flex-col border-b py-4">
        <h1 className="font-semibold text-lg text-gray-800 tracking-wide">
          Activation Link Expired
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          The activation link expires after 48 hours to protect your account.
        </p>
        <p className="mt-2 text-sm text-gray-600">
          You can request a new link to proceed.
        </p>
      </div>
      <Button
        className="mt-6 mb-5"
        outlinePrimary
        onClick={handleResendEmail}
        disabled={isResendLoading}
      >
        Request New Activation Link
      </Button>
    </ThisComponentWrapper>
  );
}

const ActivateInvalid = () => (
  <ThisComponentWrapper>
    <img src={IconInvalidActivation} alt="invalid" className="w-36" />
    <div className="flex flex-col border-b py-4">
      <h1 className="font-semibold text-lg text-gray-800 tracking-wide">
        Invalid Activation Link
      </h1>
      <p className="mt-2 text-sm text-gray-600">
        The activation link used has expired and is no longer valid.
      </p>
      <p className="mt-3 text-sm text-gray-600">
        You will need to re-register and activate your account with the new
        activation link sent to your email.
      </p>
    </div>
    <Button
      className="mt-6 mb-5"
      outlinePrimary
      onClick={() => history.push('/signup')}
    >
      Register Account
    </Button>
  </ThisComponentWrapper>
);

function ResendActivation() {
  const { id } = useParams();
  const [isResendLoading, resendEmail] = useResendEmail(
    id,
    (res) => {
      history.push({
        pathname: `/email-activation/resend/${res?.data?.uuid}`,
        state: { email_address: res?.data?.receiver || '' },
      });
    },
    () => {}
  );
  const location = useLocation();
  const handleResendEmail = () => {
    resendEmail();
  };

  return (
    <ThisComponentWrapper>
      <div className="flex flex-col w-full items-center text-center space-y-2">
        <img src={IconVerifyEmail} alt="verify" className="w-40" />
        <h1 className="font-semibold text-lg text-gray-800 tracking-wide">
          Verify Your email
        </h1>
        <p className="text-sm text-gray-600">
          We have sent an email verification link to{' '}
          <span className="font-semibold text-gray-600">
            {location?.state?.email_address || 'your email'}
          </span>{' '}
          to verify your account.
        </p>
        <div className="w-full border" />
        <div className="flex items-center text-center pt-5 space-x-1">
          <p>Didn&apos;t get the email?</p>
          <Button
            size="sm"
            transparent
            className="px-0 text-gray-500"
            disabled={isResendLoading}
            onClick={handleResendEmail}
          >
            Resend
          </Button>
        </div>
      </div>
    </ThisComponentWrapper>
  );
}

function EmailActivationRoutes() {
  const match = useRouteMatch();
  return (
    <Switch>
      <Route path={`${match.path}/success`} component={ActivateSuccess} />
      <Route path={`${match.path}/invalid`} component={ActivateInvalid} />
      <Route
        path={`${match.path}/expired/:id`}
        render={() => <ActivateExpired />}
      />
      <Route
        path={`${match.path}/resend/:id`}
        render={() => <ResendActivation />}
      />
      <Route component={PageNotFound} />
    </Switch>
  );
}

EmailActivationRoutes.propTypes = {};
ThisComponentWrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default EmailActivationRoutes;
